import * as _ from "lodash";
import PropTypes from "prop-types";
import { useState } from "react";

import { Button } from "@/components";

/**
 * Multi-step wizard with step validation and submit.
 *
 * @param root0 - The component props.
 * @param root0.steps - The array of wizard step definitions.
 * @param root0.submitTextButton - The submit button label text.
 * @param root0.onSubmit - The callback when the wizard is submitted.
 * @param root0.onValidateStep - The callback to validate the current step.
 * @returns The rendered wizard component.
 */
const Wizard = ({
    steps,
    submitTextButton = "Finish",
    onSubmit = () => null,
    onValidateStep,
}) => {
    const [currentStepIndex, setCurrentStepIndex] = useState(1);

    const currentStep = steps[currentStepIndex - 1];

    const validateCurrentStep = () => {
        let valid = true;
        const { validate, id } = currentStep;
        if (validate && onValidateStep) {
            valid = onValidateStep(id);
        }
        return valid;
    };

    const nextStep = () => {
        const validated = validateCurrentStep();
        if (validated) {
            const totalSteps = steps.length;
            let newIndex = currentStepIndex + 1;
            if (newIndex > totalSteps) {
                if (onSubmit) onSubmit();
                newIndex = totalSteps;
            }
            setCurrentStepIndex(newIndex);
        }
    };

    const backStep = () => {
        let newIndex = currentStepIndex - 1;
        if (newIndex < 1) {
            newIndex = 1;
        }
        setCurrentStepIndex(newIndex);
    };

    const renderStepIndicator = () => {
        return (
            <div className="wizard-steps-indicator-container">
                <div className="wizard-steps-indicator">
                    {_.map(steps, ({ title }, index) => {
                        let activeClass = "";
                        let doneClass = "";
                        const stepIndex = index + 1;
                        const firstItemClass = stepIndex === 1 ? "first" : "";
                        const lastItemClass =
                            stepIndex === steps.length ? "last" : "";
                        let icon = stepIndex;
                        if (currentStepIndex === stepIndex) {
                            activeClass = "active";
                        }
                        if (currentStepIndex > stepIndex) {
                            doneClass = "done";
                            icon = <i className="fa fa-check" />;
                        }
                        const componentClass = `${firstItemClass} ${lastItemClass} ${activeClass} ${doneClass}`;
                        return (
                            <div
                                key={stepIndex}
                                className={`wizard-steps-indicator-item ${componentClass}`}
                            >
                                <div className="line">
                                    <div className="side-left" />
                                    <div className="side-right" />
                                </div>
                                <div className="indicator">{icon}</div>
                                <div className="title">{title}</div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderButtons = () => {
        const nextTitle =
            currentStepIndex === steps.length ? submitTextButton : "Next Step";
        return (
            <div className="wizard-steps-buttons">
                <Button className="next" onClick={nextStep}>
                    {nextTitle}
                </Button>
                {currentStepIndex > 1 && (
                    <Button className="previous" outlined onClick={backStep}>
                        Previous Step
                    </Button>
                )}
            </div>
        );
    };

    return (
        <div className="lights-map-wizard">
            {renderStepIndicator()}
            <div className="wizard-steps-content">{currentStep.component}</div>
            {renderButtons()}
        </div>
    );
};

Wizard.propTypes = {
    steps: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired,
            component: PropTypes.node.isRequired,
            validate: PropTypes.bool,
        }),
    ),
    submitTextButton: PropTypes.string,
    onSubmit: PropTypes.func,
    onValidateStep: PropTypes.func,
};

export default Wizard;
